import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import TimeStatusBadge from "../../components/badges/TimeStatusBadge";
import SettingsContext from "../../contexts/SettingsProvider";
import { ITrip } from "../../interfaces/common-interfaces";
import { dateUtil } from "../../utility/dateUtil";
import "./display.sass";
import { useDisplayService } from "./useDisplayService";
import { useSettingsService } from "../../hooks/useSettingsService";
import { localise } from "../../utility/localizationUtil";
import MarqueeDisplay from "./MarqueeDisplay";

const Display: React.FC = () => {
  const { settings } = useContext(SettingsContext);

  const [currentTime, setCurrentTime] = useState("");

  const { fetchTrips } = useDisplayService();

  const [trips, setTrips] = useState<ITrip[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleTrips, setVisibleTrips] = useState(
    trips.slice(0, settings.splitIndex)
  );
  const [isAssamese, setIsAssamese] = useState(false);

  // Toggle language
  useEffect(() => {
    const intervalMs = (settings.languageToggleInterval || 10) * 1000;

    if (intervalMs < 1000) {
      console.warn(
        "Language toggle interval too low, defaulting to 10 seconds"
      );
    }

    const langInterval = setInterval(() => {
      setIsAssamese((prev) => !prev);
    }, intervalMs);

    return () => clearInterval(langInterval);
  }, [trips]);

  useEffect(() => {
    if (settings.ghat !== "") {
      fetchTrips(setTrips, settings.ghat); // Initial fetch

      const intervalMs = (settings.apiCallFrequency || 10) * 1000;

      if (intervalMs < 1000) {
        console.warn("API call frequency too low, defaulting to 10 seconds");
      }

      const fetchInterval = setInterval(() => {
        fetchTrips(setTrips, settings.ghat);
      }, intervalMs);

      return () => clearInterval(fetchInterval);
    } else {
      // toast.error("Ghat not found in the settings.");
      console.log("Ghat not found in the settings.");
    }
  }, [settings.ghat]);

  useEffect(() => {
    // Update time every second in IST
    const updateClock = () => {
      const now = new Date();
      const timeString = now
        .toLocaleTimeString("en-GB", {
          timeZone: "Asia/Kolkata",
          hour12: false,
        }) // IST in 24-hour format
        .padStart(8, "0"); // Ensure leading zeroes
      setCurrentTime(timeString);
    };

    const timer = setInterval(updateClock, 1000);
    updateClock(); // Set the time immediately

    return () => clearInterval(timer); // Cleanup interval
  }, []);

  useEffect(() => {
    const intervalMs = (settings.slideInDuration || 5) * 1000;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex =
          prevIndex + settings.splitIndex >= trips.length
            ? 0
            : prevIndex + settings.splitIndex;
        setVisibleTrips(
          trips.slice(nextIndex, nextIndex + settings.splitIndex)
        );
        return nextIndex;
      });
    }, intervalMs);

    return () => clearInterval(interval);
  }, [trips]);

  // Total pages (ceil of trips.length / settings.splitIndex)
  const totalPages = Math.ceil(trips.length / settings.splitIndex);

  return (
    <div className="bg-slate-800 text-white min-h-screen">
      <div className="px-20 py-10">
        <div className="flex justify-between items-center text-[#FFD233] mb-8">
          <div>
            <div
              className={`flex items-center gap-x-4 font-semibold mb-2`}
              style={{ fontSize: `${settings.header1FontSize}px` }}
            >
              <div className={`flex items-center gap-x-4 font-semibold`}>
                <DirectionsBoatIcon fontSize="inherit" />
                <span className="">
                  {isAssamese ? "প্ৰস্থানসমূহ" : "DEPARTURES"}
                </span>
              </div>
              <div className="flex">
                (
                <div
                  className={`flex items-center gap-x-4 font-semibold`}
                  style={{ fontSize: `${settings.header2FontSize}px` }}
                >
                  <DirectionsWalkIcon
                    fontSize="inherit"
                    className="walking-icon"
                  />
                  <span className="">
                    {isAssamese ? "বাৰ্থলৈ খোজ কঢ়াৰ সময় - ৫ মিনিট" : "WALKING TIME TO BERTH - 5 MINUTES"}
                  </span>
                </div>
                )
              </div>
            </div>
            <div
              className={`flex items-center gap-x-4 font-semibold mb-2`}
              style={{ fontSize: `${settings.header2FontSize}px` }}
            >
              {localise(isAssamese ? "as" : "en", settings.ghatName)}
            </div>
          </div>

          <div
            className={`flex flex-col items-end gap-y-2`}
            style={{ fontSize: `${settings.header2FontSize}px` }}
          >
            <div className="font-semibold flex gap-4 items-center">
              <AccessTimeFilledIcon fontSize="inherit" />
              <span>{currentTime}</span>
            </div>
            <div className="font-semibold">
              {dateUtil.formatDateWithDay(new Date().toISOString())}
            </div>
          </div>
        </div>
        <hr />
        <div className="mt-12">
          <table>
            <thead>
              <tr>
                <th
                  style={{
                    fontSize: `${settings.tableBodyFontSize}px`,
                    width: "30%",
                  }}
                >
                  {isAssamese ? "গন্তব্যস্থান" : "DESTINATION"}
                </th>
                <th
                  style={{
                    fontSize: `${settings.tableBodyFontSize}px`,
                    width: "20%",
                  }}
                >
                  {isAssamese ? "জাহাজ" : "VESSEL"}
                </th>
                <th
                  style={{
                    fontSize: `${settings.tableBodyFontSize}px`,
                    width: "10%",
                  }}
                >
                  {isAssamese ? "বাৰ্থ" : "BERTH"}
                </th>
                <th
                  style={{
                    fontSize: `${settings.tableBodyFontSize}px`,
                    width: "12.5%",
                  }}
                >
                  {isAssamese ? "প্ৰস্থানৰ সময়" : "DEPARTURE"}
                </th>
                <th
                  style={{
                    fontSize: `${settings.tableBodyFontSize}px`,
                    width: "12.5%",
                  }}
                >
                  {isAssamese ? "সময়কাল" : "DURATION"}
                </th>
                <th
                  className={`text-center`}
                  style={{
                    fontSize: `${settings.tableBodyFontSize}px`,
                    width: "35%",
                  }}
                >
                  {isAssamese ? "স্থিতি" : "STATUS"}
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleTrips.map((trip, index) => (
                <tr key={trip.id || index}>
                  <td style={{ fontSize: `${settings.tableBodyFontSize}px` }}>
                    {localise(
                      isAssamese ? "as" : "en",
                      trip.route.ghatTo.ghatName
                    )}
                  </td>
                  <td style={{ fontSize: `${settings.tableBodyFontSize}px` }}>
                    {localise(isAssamese ? "as" : "en", trip.vessel.name)}
                  </td>
                  <td style={{ fontSize: `${settings.tableBodyFontSize}px` }}>
                    {localise(isAssamese ? "as" : "en", trip.dockNo)}
                  </td>
                  <td style={{ fontSize: `${settings.tableBodyFontSize}px` }}>
                    {dateUtil.formatTime(trip.departureTime)}
                  </td>
                  <td style={{ fontSize: `${settings.tableBodyFontSize}px` }}>
                    {dateUtil.formatDuration(trip.tripDuration)}
                  </td>
                  <td style={{ fontSize: `${settings.tableBodyFontSize}px` }}>
                    <TimeStatusBadge timeStatus={trip.timeStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {settings.displayTextList && settings.displayTextList.length > 0 && (
          <MarqueeDisplay
            fontSize={Number(settings.header1FontSize)}
            messages={settings.displayTextList}
          />
        )}

        {/* Page Indicators */}
        <div className="mt-8 absolute bottom-20 left-1/2">
          <div className="flex justify-center items-center gap-x-2 py-4">
            {Array.from({ length: totalPages }).map((_, pageIndex) => (
              <div
                key={pageIndex}
                className={`w-4 h-4 rounded-full border-2 transition ${currentIndex / settings.splitIndex === pageIndex
                  ? "bg-[#FFD233] border-[#FFD233]"
                  : "bg-transparent border-gray-400"
                  }`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Display;
